import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/common";

function StoryForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState<any>({ name: "", email: "", location: "", honoring: "", rating: 5, story: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/memorial/stories", f); onDone(); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-8 border border-neutral-200 bg-white p-6" data-testid="story-form">
      <Field label="Your name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="story-name" /></Field>
      <Field label="Email (not shown)"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="story-email" /></Field>
      <Field label="Location (optional)"><input value={f.location} onChange={set("location")} placeholder="e.g. Sydney, NSW" className={inputCls} data-testid="story-location" /></Field>
      <Field label="Honoring (optional)"><input value={f.honoring} onChange={set("honoring")} placeholder="e.g. my mother" className={inputCls} data-testid="story-honoring" /></Field>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={set("website")} />
      <div className="sm:col-span-2 flex items-center gap-1" data-testid="story-rating">
        {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} aria-label={`${n} stars`} data-testid={`story-star-${n}`}><Star size={20} className={n <= f.rating ? "fill-[var(--c-primary)] text-[var(--c-primary)]" : "text-neutral-300"} /></button>)}
      </div>
      <div className="sm:col-span-2"><Field label="Your story"><textarea required minLength={20} rows={5} value={f.story} onChange={set("story")} className={inputCls} data-testid="story-text" /></Field></div>
      <button disabled={busy} className="btn-ink sm:col-span-2 py-3.5 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60" data-testid="story-submit">{busy ? "Sending…" : "Share Your Story"}</button>
    </form>
  );
}

export function MemorialStories({ b }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/memorial/stories").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-20" data-testid="memorial-stories">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl">{b.heading || "Family Stories"}</h2>
        {b.intro && <p className="mt-4 text-neutral-600">{b.intro}</p>}
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {items.slice(0, b.count || 6).map((s) => (
          <figure key={s.id} className="bg-white border border-neutral-200 p-8 flex flex-col" data-testid={`story-${s.id}`}>
            <div className="flex gap-0.5 mb-4">{Array.from({ length: s.rating || 5 }).map((_, i) => <Star key={i} size={14} className="fill-[var(--c-primary)] text-[var(--c-primary)]" />)}</div>
            <blockquote className="font-display text-xl leading-snug flex-1">&ldquo;{s.story}&rdquo;</blockquote>
            <figcaption className="mt-6 text-xs uppercase tracking-[0.15em]">{s.name}{s.location && <span className="text-neutral-500"> · {s.location}</span>}{s.honoring && <span className="block normal-case tracking-normal text-neutral-500 mt-1">In memory of {s.honoring}</span>}</figcaption>
          </figure>
        ))}
      </div>
      <div className="max-w-3xl mx-auto text-center mt-10">
        {sent ? <p className="text-sm" data-testid="story-sent">Thank you for sharing. Your story will appear once our team has reviewed it.</p>
          : open ? <StoryForm onDone={() => { setSent(true); setOpen(false); }} />
          : <button onClick={() => setOpen(true)} className="btn-line px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase" data-testid="story-open-form">Share Your Story</button>}
      </div>
    </section>
  );
}
